import { NgClass, NgForOf, NgIf } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  Output
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import { PendoService } from '../../../shared/services/analytics/pendo.service';
import { DataRulesConfirmDialogComponent } from '../../data-rules-confirm-dialog/data-rules-confirm-dialog.component';
import { SkeletonComponent } from '../../../shared/skeleton/skeleton.component';
import { ISidebarFolder } from '../../data-rules-model';
import { IRuleList } from '../../lib/folder-popup/folder-popup.component';
import { appearFromAbove } from '../../rule/kyloe-animations';
import { TooltipOnTruncateDirective } from '../../../shared/directives/tooltip-on-truncate.directive';

@Component({
  selector: 'app-folders-sidebar',
  standalone: true,
  templateUrl: './folders-sidebar.component.html',
  styleUrls: ['./folders-sidebar.component.scss'],
  imports: [NgClass, NgForOf, NgIf, MatTooltip, SkeletonComponent, TooltipOnTruncateDirective],
  animations: [appearFromAbove]
})
export class FoldersSidebarComponent implements OnDestroy {
  @Input() userFolders: ISidebarFolder[] = [];
  @Input() systemItems: ISidebarFolder[] = [];
  @Input() productFolders: ISidebarFolder[] = [];
  @Input() scheduledCount = 0;
  @Input() selectedFolderId = 'all';
  @Input() loading = false;
  @Input() allRules: IRuleList[] = [];
  @Input() folderRulesMap = new Map<string, IRuleList[]>();

  @Output() folderSelected = new EventEmitter<string>();
  @Output() folderRenamed = new EventEmitter<{ folderId: string; name: string }>();
  @Output() deleteFolder = new EventEmitter<ISidebarFolder>();
  @Output() ruleAddedToFolder = new EventEmitter<{ folderId: string; rule: IRuleList }>();
  @Output() ruleRemovedFromFolder = new EventEmitter<{ folderId: string; rule: IRuleList }>();
  @Output() readonly popupStateChange = new EventEmitter<{ folder: ISidebarFolder; folderRules: IRuleList[] } | null>();
  @Output() readonly createFolderPopupOpened = new EventEmitter<void>();

  @HostBinding('class.collapsed') collapsed = false;
  hoveredFolderId: string | null = null;
  hoveredBadgeId: string | null = null;

  activeFolderForPopup: ISidebarFolder | null = null;
  activeFolderRules: IRuleList[] = [];
  private activeTriggerButton: HTMLElement | null = null;
  private createFolderTriggerButton: HTMLElement | null = null;

  constructor(private el: ElementRef<HTMLElement>, private dialog: MatDialog, private pendoService: PendoService) {}

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const tag = (document.activeElement as HTMLElement)?.tagName?.toLowerCase();
    if ((event.key === '[' || event.key === ']') && tag !== 'input' && tag !== 'textarea') {
      this.toggleCollapsed('keyboard');
    }
    if (event.key === 'Escape' && this.activeFolderForPopup) {
      const tag = (document.activeElement as HTMLElement)?.tagName?.toLowerCase();
      if (tag !== 'input' && tag !== 'textarea') {
        this.closePopup();
      }
    }
  }

  trackById(_index: number, folder: ISidebarFolder): string {
    return folder.id;
  }

  get hasProductRules(): boolean {
    return this.productFolders.some(f => f.count > 0);
  }

  isSystemFolderDisabled(folder: ISidebarFolder): boolean {
    return folder.id === 'archive' && folder.count === 0;
  }

  selectFolder(id: string): void {
    this.folderSelected.emit(id);
  }

  onSystemFolderClick(folder: ISidebarFolder): void {
    if (this.isSystemFolderDisabled(folder)) {
      return;
    }
    this.selectFolder(folder.id);
  }

  toggleCollapsed(trigger: 'mouse' | 'keyboard' = 'mouse'): void {
    if (!this.collapsed) {
      const badges = Array.from(this.el.nativeElement.querySelectorAll<HTMLElement>('.folder-badge'));
      const maxBadgeWidth = badges.reduce((max, b) => Math.max(max, b.offsetWidth), 0) || 30;
      const collapsedWidth = maxBadgeWidth + 49;
      this.el.nativeElement.style.setProperty('--collapsed-width', `${collapsedWidth}px`);
    }
    this.collapsed = !this.collapsed;
    this.trackSidebarToggled(trigger);
  }

  openFolderPopup(folder: ISidebarFolder, event: MouseEvent): void {
    this.setActiveFolder(event.currentTarget as HTMLElement, folder);
    this.trackManageRulesPopupOpened(this.activeFolderRules.length);
  }

  openPopupForActiveFolder(): void {
    const button = this.el.nativeElement.querySelector<HTMLElement>('.folder-item.active .folder-row-action');
    if (this.activeTriggerButton) {
      this.activeTriggerButton.style.removeProperty('anchor-name');
    }
    if (!button) {
      return;
    }
    const folder = this.userFolders.find(f => f.id === this.selectedFolderId);
    if (!folder) {
      return;
    }
    this.setActiveFolder(button, folder);
  }

  private setActiveFolder(button: HTMLElement, folder: ISidebarFolder): void {
    if (this.activeTriggerButton) {
      this.activeTriggerButton.style.removeProperty('anchor-name');
    }
    this.activeTriggerButton = button;
    this.activeTriggerButton.style.setProperty('anchor-name', '--folder-popup, --folder-popup-arrow');
    this.activeFolderForPopup = folder;
    this.activeFolderRules = this.folderRulesMap.get(folder.id) ?? [];
    this.popupStateChange.emit({ folder: this.activeFolderForPopup, folderRules: this.activeFolderRules });
  }

  closePopup(): void {
    if (this.activeTriggerButton) {
      this.activeTriggerButton.style.removeProperty('anchor-name');
      this.activeTriggerButton = null;
    }
    this.activeFolderForPopup = null;
    this.activeFolderRules = [];
    this.popupStateChange.emit(null);
  }

  openCreateFolderPopup(event: MouseEvent): void {
    if (this.createFolderTriggerButton) {
      this.createFolderTriggerButton.style.removeProperty('anchor-name');
    }
    this.createFolderTriggerButton = event.currentTarget as HTMLElement;
    this.createFolderTriggerButton.style.setProperty('anchor-name', '--folder-popup, --folder-popup-arrow');
    this.createFolderPopupOpened.emit();
  }

  closeCreateFolderPopup(): void {
    if (this.createFolderTriggerButton) {
      this.createFolderTriggerButton.style.removeProperty('anchor-name');
      this.createFolderTriggerButton = null;
    }
  }

  onFolderRenamed(name: string): void {
    this.folderRenamed.emit({ folderId: this.activeFolderForPopup.id, name });
    this.activeFolderForPopup = { ...this.activeFolderForPopup, name };
    this.popupStateChange.emit({ folder: this.activeFolderForPopup, folderRules: this.activeFolderRules });
  }

  onDeleteRequested(): void {
    const folder = this.activeFolderForPopup;
    if (!folder) {
      return;
    }
    const folderRuleCount = this.activeFolderRules.length;
    this.closePopup();
    const dialogRef = this.dialog.open(DataRulesConfirmDialogComponent, {
      width: '45vw',
      height: 'auto',
      autoFocus: true,
      data: {
        titleIcon: 'bhi-delete-o',
        title: `Delete the "${folder.name}" folder?`,
        messageIcon: 'info_outlined',
        message: 'No rules will be deleted, they will just be disconnected from this folder'
      }
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      this.trackDeleteFolderConfirmation(!!confirmed, folderRuleCount);
      if (!confirmed) {
        return;
      }
      this.deleteFolder.emit(folder);
    });
  }

  onRuleAdded(rule: IRuleList): void {
    if (!this.activeFolderForPopup) {
      return;
    }
    this.ruleAddedToFolder.emit({ folderId: this.activeFolderForPopup.id, rule });
    this.activeFolderRules = [...this.activeFolderRules, rule];
    this.popupStateChange.emit({ folder: this.activeFolderForPopup, folderRules: this.activeFolderRules });
  }

  onRuleRemoved(rule: IRuleList): void {
    if (!this.activeFolderForPopup) {
      return;
    }
    this.ruleRemovedFromFolder.emit({ folderId: this.activeFolderForPopup.id, rule });
    this.activeFolderRules = this.activeFolderRules.filter(r => r.id !== rule.id);
    this.popupStateChange.emit({ folder: this.activeFolderForPopup, folderRules: this.activeFolderRules });
  }

  ngOnDestroy(): void {
    this.closePopup();
    this.closeCreateFolderPopup();
  }

  // ---------------------------------------------------------------------------
  // Pendo Track Events
  // ---------------------------------------------------------------------------

  private trackSidebarToggled(trigger: 'mouse' | 'keyboard'): void {
    this.recordPendoEvent('DataRules Folders | Sidebar Toggled', {
      state: this.collapsed ? 'collapsed' : 'expanded',
      trigger
    });
  }

  private trackManageRulesPopupOpened(folderRuleCount: number): void {
    this.recordPendoEvent('DataRules Folders | Folder Popup Opened', {
      source: 'sidebar-folder-item',
      mode: 'manage-rules',
      folderRuleCount
    });
  }

  private trackDeleteFolderConfirmation(confirmed: boolean, folderRuleCount: number): void {
    this.recordPendoEvent('DataRules Folders | Delete Folder Confirmation', {
      confirmed,
      folderRuleCount
    });
  }

  private recordPendoEvent(name: string, data: Record<string, unknown>): void {
    // console.log(`%c ${name}`, 'background: #00B398; color: #BADA55; font-size:22px', data);
    this.pendoService?.recordEvent(name, data);
  }
}
